import { useState } from "react";
import { toast } from "sonner";
import { Inbox, BellRing, MailSearch, Send, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, useOwedUnlocked } from "@/lib/api";
import { useMe } from "@/lib/me";
import { PageHead, StatusBadge, TypeTag, Empty } from "@/components/Bits";
import QueryDrawer from "@/components/QueryDrawer";
import { gbp } from "@/lib/format";

const TABS = [["open", "Open"], ["replied", "Replied"], ["escalated", "Escalated"], ["awaiting_supplier", "Awaiting"], ["resolved", "Resolved"], ["", "All"]];
const ACCENT = { open: "bg-sky-500", replied: "bg-teal-500", escalated: "bg-rose-500", awaiting_supplier: "bg-amber-500", resolved: "bg-emerald-500" };

const Stat = ({ label, value, tone, testid }) => (
  <div className="min-w-[132px] snap-start rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 font-display text-3xl font-extrabold ${tone}`}>{value ?? 0}</p>
  </div>
);

function Action({ icon: Icon, label, run, testid, disabled }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button variant="outline" disabled={busy || disabled} data-testid={testid}
      onClick={async () => { setBusy(true); try { await run(); } finally { setBusy(false); } }}>
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icon className="mr-2 h-4 w-4" />} {label}
    </Button>
  );
}

export default function QueriesPage() {
  const api = useApi();
  const me = useMe();
  const [tab, setTab] = useState("open");
  const [open, setOpen] = useState(null);
  const rep = useLoad("/report");
  const list = useLoad(`/queries?status=${tab}`);
  const refresh = () => { rep.reload(); list.reload(); };
  const c = rep.data?.counts || {};
  const unlocked = useOwedUnlocked();
  const act = (path, msg) => async () => {
    try { const r = await api("post", path); toast.success(msg(r)); refresh(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <PageHead title="Queries" sub="Discrepancies raised with wholesalers and where each one stands.">
        <div className="-mx-4 flex w-[calc(100%+2rem)] gap-2 overflow-x-auto px-4 pb-1 sm:w-auto [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&>button]:shrink-0" data-testid="query-actions">
        <Action icon={BellRing} label="Send due chasers" testid="send-followups-button" run={act("/followup", (r) => `${r.chasers_sent} chaser(s) sent, ${r.escalated} escalated`)} />
        <Action icon={MailSearch} label="Check inbox" testid="poll-inbox-button" disabled={!me.features.inbox_reading} run={act("/inbox/poll", (r) => `${r.replies_attached} reply(ies) matched`)} />
        <Action icon={Send} label="Email me the digest" testid="send-digest-button" run={act("/digest", () => "Digest sent to your email")} />
        <Action icon={Download} label="Download open queries" testid="export-queries-button" run={async () => {
          try {
            const blob = await api("get", "/queries/export?status=open", null, { responseType: "blob" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob); a.download = `open-queries-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
            URL.revokeObjectURL(a.href);
          } catch (e) { toast.error(e.message); }
        }} />
        </div>
      </PageHead>

      <div className="-mx-4 mb-8 flex snap-x gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:grid sm:grid-cols-3 sm:px-0 lg:grid-cols-6 rise" data-testid="query-stats">
        <Stat label="Open" value={rep.data?.open} tone="text-slate-900" testid="stat-open" />
        <Stat label="Owed back" value={unlocked && rep.data?.owed_total != null ? gbp(rep.data.owed_total) : "Locked"} tone={unlocked ? "text-rose-600" : "text-slate-400"} testid="stat-owed" />
        <Stat label="Supplier replied" value={c.replied} tone="text-teal-700" testid="stat-replied" />
        <Stat label="Escalated" value={c.escalated} tone="text-rose-600" testid="stat-escalated" />
        <Stat label="Chasers due" value={rep.data?.chasers_due} tone="text-amber-600" testid="stat-chasers-due" />
        <Stat label="Resolved" value={c.resolved} tone="text-emerald-600" testid="stat-resolved" />
      </div>

      <div className="-mx-4 mb-4 flex gap-1 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:px-0">
        {TABS.map(([v, l]) => (
          <button key={l} onClick={() => setTab(v)} data-testid={`query-filter-${l.toLowerCase()}`}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}
      </div>

      {list.data?.length === 0 && <Empty icon={Inbox} title="Nothing here" text="Import a statement and reconcile to raise queries." testid="queries-empty" />}
      <div className="space-y-2" data-testid="queries-list">
        {(list.data || []).map((q) => (
          <button key={q.thread} onClick={() => setOpen(q.thread)} data-testid={`query-item-row-${q.thread}`}
            className={`relative flex w-full flex-col gap-2 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 pl-5 text-left transition-colors hover:border-slate-400 sm:flex-row sm:items-center sm:gap-4`}>
            <span className={`absolute inset-y-0 left-0 w-1 ${ACCENT[q.status] || "bg-slate-400"}`} />
            <div className="flex items-center justify-between gap-2 sm:w-28">
              <span className="font-mono text-sm font-semibold text-slate-900">{q.thread}</span>
              <span className="sm:hidden"><StatusBadge status={q.status} /></span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2"><span className="font-semibold">{q.supplier}</span><span className="text-xs text-slate-400">{q.month}</span><TypeTag type={q.type} /></div>
              <p className="mt-1 line-clamp-2 text-sm text-slate-600 sm:truncate">{q.detail}</p>
            </div>
            <div className="flex items-center gap-3">
              {unlocked && q.at_stake > 0 && <span className="font-mono text-sm font-bold text-rose-600" data-testid={`query-row-amount-${q.thread}`}>{gbp(q.at_stake)}</span>}
              {q.followups > 0 && <span className="text-xs text-slate-500">{q.followups} chaser{q.followups > 1 ? "s" : ""}</span>}
              <span className="hidden sm:inline"><StatusBadge status={q.status} /></span>
            </div>
          </button>
        ))}
      </div>
      <QueryDrawer thread={open} onClose={() => setOpen(null)} onChanged={refresh} />
    </div>
  );
}
